import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxFormMode, OnyxOption, OnyxSuggestion } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  OnyxDatepickerComponent,
  onyxDateValidator,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  onyxMaxDateValidator,
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  onyxVinNumberValidator,
} from '@onyx/angular';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { SuggestionHelper } from '../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { FleetType } from '../../../../common/interfaces/validation/fleet-validation';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { BaseHelper } from '../../../management-panel/bases/common/helpers/base.helper';
import { FleetCategory } from '../../common/enums/fleet-category';
import { FleetState } from '../../common/enums/fleet-state';
import { VehiclePlanningMode } from '../../common/enums/vehicle-planning-mode';

type FleetGeneralInformationFormGroup = ReturnType<
  typeof FleetGeneralInformationFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-general-information-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDatepickerComponent,
    OnyxDropdownComponent,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxAutoFocusDirective,
    AsyncPipe,
  ],
  templateUrl: './fleet-general-information-form.component.html',
  styleUrl: './fleet-general-information-form.component.scss',
})
export class FleetGeneralInformationFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly baseHelper = inject(BaseHelper);
  private readonly suggestionsHelper = inject(SuggestionHelper);

  public readonly form = input.required<FleetGeneralInformationFormGroup>();
  public readonly validation = input.required<ValidationGroup>();
  public readonly category = input.required<FleetCategory>();
  public readonly mode = input.required<OnyxFormMode>();

  protected readonly I18N = 'fleet.fleetForm.generalInformation';
  protected readonly MANUFACTURE_YEAR_SUGGESTIONS: OnyxSuggestion<number>[] =
    Array.from({ length: 3 }, (_, index) => {
      const year = DateTime.now().year - (2 - index);
      return { name: `${year}`, value: year };
    });
  protected readonly MAX_SPEED_SUGGESTIONS: OnyxSuggestion<number>[] = [
    85, 89,
  ].map((value) => ({
    name: `${value}`,
    value,
  }));

  protected readonly baseSuggestions$ = this.suggestionsHelper.baseSuggestions$;
  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );
  protected readonly states$ = this.dictionariesService.getDictionary(
    DictionaryCode.FLEET_STATE,
  );
  protected readonly planningModes$ = this.dictionariesService.getDictionary(
    DictionaryCode.VEHICLE_PLANNING_MODE,
  );
  protected readonly BASES_SOURCE = this.baseHelper.getBasesSource();

  protected readonly baseDropdownActions = this.baseHelper.getDropdownActions(
    () => this.form().controls.assignedBase,
  );

  protected makeSuggestions = computed(() => {
    const buildSuggestions = (keys: string[]) =>
      keys.map((key) => ({
        name: key,
        value: key,
      }));

    return {
      [FleetCategory.SEMI_TRUCK]: buildSuggestions(['DAF', 'Scania']),
      [FleetCategory.SEMI_TRAILER]: buildSuggestions([
        'Schmitz Cargobull',
        'Krone',
      ]),
      [FleetCategory.TRAILER]: buildSuggestions(['Wielton']),
      [FleetCategory.STRAIGHT_TRUCK]: buildSuggestions([
        'MAN',
        'Mercedes-Benz',
      ]),
      [FleetCategory.VAN]: buildSuggestions(['Volkswagen', 'Ford']),
    }[this.category()]!;
  });

  protected makes$!: Observable<OnyxOption<string>[]>;

  public ngOnInit(): void {
    this.makes$ = this.dictionariesService.getDictionary(
      this.category().includes('trailer')
        ? DictionaryCode.TRAILER_MAKE
        : DictionaryCode.VEHICLE_MAKE,
    );

    effect(
      () => {
        const controls = this.form().controls;
        const fields = this.validation().fields;

        setTimeout(() => {
          ValidationHelper.updateControl(
            controls.manufactureYear,
            fields['manufactureYear'],
          );
          ValidationHelper.updateControl(
            controls.planningMode,
            fields['planningMode'],
          );
          ValidationHelper.updateControl(
            controls.initialMileage,
            fields['initialMileage'],
          );
          ValidationHelper.updateControl(controls.maxSpeed, fields['maxSpeed']);
          ValidationHelper.updateControl(
            controls.assignedBase,
            fields['assignedBase'],
          );
        });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      category: fb.control(
        // Always provided by resolver
        { value: null as unknown as FleetCategory, disabled: true },
        [Validators.required],
      ),
      type: fb.control<FleetType | null>({ value: null, disabled: true }), // Provided by resolver
      registrationNumber: fb.control<string | null>(null, [
        Validators.required,
        Validators.minLength(4),
        onyxTextFieldLengthValidator,
      ]),
      sideNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      vinNumber: fb.control('', [Validators.required, onyxVinNumberValidator]),
      make: fb.control<string | null>(null, [Validators.required]),
      model: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      manufactureYear: fb.control<number | null>(null, [Validators.required]),
      firstRegistrationDate: fb.control<string | null>(null, [
        Validators.required,
        onyxDateValidator,
        onyxMaxDateValidator(),
      ]),
      registrationCountry: fb.control(preferencesService.defaultCountry(), [
        Validators.required,
      ]),
      state: fb.control(FleetState.ACTIVE, [Validators.required]),
      planningMode: fb.control<VehiclePlanningMode | null>(
        VehiclePlanningMode.AUTO,
      ),
      initialMileage: fb.control<number | null>(null, [
        onyxMinNumberValidator(0),
      ]),
      maxSpeed: fb.control<number | null>(null, [
        onyxMinNumberValidator(1),
        onyxTextFieldLengthValidator,
      ]),
      assignedBase: fb.control<string | null>(null),
    });
  }
}
